import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { createEventRequestThunk } from '../redux/createEventRequestThunk';
import { resetEventRequestStatus } from '../redux/eventRequestSlice';
import { fetchActivitiesThunk } from '../redux/fetchProjectsThunk'; // ✅ import thunk

const AwarenessActivitiesTab = ({ item, category_id = 1 }) => {
  const [selectedActivities, setSelectedActivities] = useState({});
  const dispatch = useDispatch();

  const categoryKey = category_id === 1 ? 'awareness' : 'celebration';

  const {
    activities: {
      list: allActivities = [],
      loading: activityLoading,
      error: activityError,
    } = {}
  } = useSelector((state) => state.projects.eventDetails[categoryKey]);

  const { loading, success, error } = useSelector((state) => state.eventRequest);

  // 🔽 New: ref for submit button section
  const submitButtonRef = useRef(null);

  useEffect(() => {
    if (item?.awareness_id) {
      dispatch(fetchActivitiesThunk({ category_id, event_id: item.awareness_id }));
    }
  }, [dispatch, category_id, item?.awareness_id]);

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        dispatch(resetEventRequestStatus());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, error, dispatch]);

  const handleCheckboxChange = (activityId) => {
    setSelectedActivities((prev) => {
      const newSelected = {
        ...prev,
        [activityId]: !prev[activityId],
      };

      // 🔽 New: scroll to submit button when any activity is selected
      if (Object.values(newSelected).some(Boolean)) {
        setTimeout(() => {
          submitButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }

      return newSelected;
    });
  };

  const handleSubmitActivities = () => {
    const selected = allActivities.filter((activity) => selectedActivities[activity.id]);
    console.log("Selected Activities:", selected);

    if (selected.length === 0) {
      alert('Please select at least one activity.');
      return;
    }

    const formattedDate = moment(item.date, "DD-MM-YYYY").format("YYYY-MM-DD");

    const payload = {
      event_name: item.name || "Unnamed Event",
      event_date: formattedDate,
      activities: selected.map((act) => ({
        activity_name: act.activity || "",
        activity_desc: act.activity_desc || act.description || "",
      })),
    };

    dispatch(createEventRequestThunk(payload));
    setSelectedActivities({});
  };

  const hasSelectedActivities = Object.values(selectedActivities).some(Boolean);
  const applyMultiColumn = allActivities.length > 10;

  return (
    <div className="card card-body">
      <h3 className='text-18'>Activities for {item.name || item.details}</h3>
      <p>Select activities to add to this awareness day's schedule.</p>

      {activityLoading ? (
        <p>Loading activities...</p>
      ) : activityError ? (
        <div className="alert alert-danger">Error loading activities: {activityError.message || activityError}</div>
      ) : allActivities.length > 0 ? (
        <>
          <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #e0e0e0', padding: '10px' }}>
            <div
              className="activities-list"
              style={applyMultiColumn ? { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '15px' } : {}}
            >
              {allActivities.map((activity) => (
                <div className="form-check mb-2" key={activity.id}>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`activity-${activity.id}`}
                    checked={!!selectedActivities[activity.id]}
                    onChange={() => handleCheckboxChange(activity.id)}
                  />
                  <label className="form-check-label" htmlFor={`activity-${activity.id}`}>
                    <strong>{activity.activity || activity.name}</strong>
                    <br />
                    <small className="text-muted">{activity.activity_desc}</small>
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* 🔽 Add ref to the submit button section */}
          {hasSelectedActivities && (
            <div className="d-grid gap-2 mt-4" ref={submitButtonRef}>
              <button
                className="btn btn-success btn-lg"
                onClick={handleSubmitActivities}
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Submit Selected Activities'}
              </button>
            </div>
          )}

          {success && (
            <div className="alert alert-success mt-3">
              Event submitted successfully!
            </div>
          )}

          {error && (
            <div className="alert alert-danger mt-3">
              Error: {error}
            </div>
          )}
        </>
      ) : (
        <div className="alert alert-info mt-3">
          <h4 className="alert-heading">No activities found for "{item.name || item.details}"</h4>
          <p>This event does not have any activities loaded from the server yet.</p>
        </div>
      )}
    </div>
  );
};

export default AwarenessActivitiesTab;
